import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { MessageService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import {
    CustomerOrderHeader,
    CustomerOrderLine,
    StaffCustomerViewService
} from '../services/staff-customer-view.service';
import { openInvoiceView } from './customer-support-invoice.util';

@Component({
    selector: 'app-customer-support-order-detail',
    standalone: true,
    imports: [CommonModule, RouterModule, TableModule, ButtonModule, TagModule, ToastModule, ToolbarModule],
    providers: [MessageService],
    templateUrl: './customer-support-order-detail.component.html',
    styleUrl: './customer-support-order-detail.component.scss'
})
export class CustomerSupportOrderDetailComponent implements OnInit {
    customerId = '';
    orderNumber = '';
    company = '';
    division = '';

    header: CustomerOrderHeader | null = null;
    lines: CustomerOrderLine[] = [];
    loading = false;
    invoiceLoading = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private staffCustomerViewService: StaffCustomerViewService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.customerId = this.route.snapshot.paramMap.get('customerId') || '';
        this.orderNumber = this.route.snapshot.paramMap.get('orderNumber') || '';
        this.company = this.route.snapshot.queryParamMap.get('company') || '';
        this.division = this.route.snapshot.queryParamMap.get('division') || '';

        if (!this.customerId || !this.orderNumber) {
            this.backToSupport();
            return;
        }
        this.load();
    }

    load(): void {
        if (!this.company || !this.division) {
            this.messageService.add({
                severity: 'error',
                summary: 'Missing scope',
                detail: 'Company and division are required to load order detail.'
            });
            return;
        }

        this.loading = true;
        forkJoin({
            header: this.staffCustomerViewService.getOrderDetail(
                this.customerId,
                this.orderNumber,
                this.company,
                this.division
            ),
            lines: this.staffCustomerViewService.getOrderLines(
                this.customerId,
                this.orderNumber,
                this.company,
                this.division
            )
        })
            .pipe(finalize(() => (this.loading = false)))
            .subscribe({
                next: ({ header, lines }) => {
                    this.header = header;
                    this.lines = lines.content ?? [];
                    if (!this.company) this.company = header.companyId;
                    if (!this.division) this.division = header.divisionId;
                },
                error: (err) => this.showError('Failed to load order', err)
            });
    }

    viewInvoice(): void {
        this.invoiceLoading = true;
        this.staffCustomerViewService
            .getInvoiceView(this.customerId, this.orderNumber)
            .pipe(finalize(() => (this.invoiceLoading = false)))
            .subscribe({
                next: (view) => {
                    const result = openInvoiceView(view);
                    if (result !== 'opened') {
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Invoice',
                            detail:
                                result === 'empty'
                                    ? 'Invoice PDF was empty'
                                    : `No invoice available for ${this.header?.invoiceNumber || this.orderNumber}`
                        });
                    }
                },
                error: (err) => this.showError('Invoice view failed', err)
            });
    }

    backToSupport(): void {
        this.router.navigate(['/customer-support'], {
            queryParams: { customerId: this.customerId || undefined }
        });
    }

    private showError(summary: string, err: unknown): void {
        const detail =
            typeof (err as { error?: unknown })?.error === 'string'
                ? ((err as { error: string }).error as string)
                : (err as { message?: string })?.message || 'Unexpected error';
        this.messageService.add({ severity: 'error', summary, detail });
    }
}
